import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Search, Download, RefreshCw, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/admin-api';
import { formatDateTime, formatRelative, statusBadgeClass } from './formatters';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'interview', label: 'Interview' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'archived', label: 'Archived' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hoisted out of render — the filterable status chips never change, so there
// is no need to recompute this on every render.
const STATUS_CHIPS = STATUS_OPTIONS.filter((option) => option.value !== 'all');

export default function AdminApplications() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const response = await adminApi.get('/applications', { params });
      setItems(response.data.items || []);
      setCounts(response.data.counts_by_status || {});
      setTotal(response.data.total || 0);
    } catch (_e) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (event) => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/applications.csv`, {
        credentials: 'include',
      });
      if (!response.ok) {
        toast.error('Export failed');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `omc_applications_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (_e) {
      toast.error('Export failed');
    }
  };

  return (
    <AdminLayout
      title="Job Applications"
      subtitle={`${total} application${total === 1 ? '' : 's'} on file. Click any row to view details and update status.`}
    >
      {/* Status counts chips */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-applications-status-counts">
        {STATUS_CHIPS.map((option) => (
          <button
            key={option.value}
            onClick={() => setStatusFilter(option.value)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
              statusFilter === option.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-bone-2 hover:border-primary/40 text-foreground/70'
            }`}
            data-testid={`status-chip-${option.value}`}
          >
            {option.label}
            <span className="font-mono text-[10px] tracking-wider opacity-70">
              {counts[option.value] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <form onSubmit={handleSearch} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search name, email, role, phone…"
            className="pl-9"
            data-testid="admin-applications-search"
          />
        </form>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-[180px]" data-testid="admin-applications-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchData} className="gap-1.5" data-testid="admin-applications-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </Button>
        <Button variant="outline" onClick={handleExport} className="gap-1.5" data-testid="admin-applications-export">
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </Button>
      </div>

      {/* Table */}
      <Card className="editorial-card p-0">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-12 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center" data-testid="admin-applications-empty">
              <p className="text-muted-foreground">No applications match the current filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-applications-table">
                <thead className="bg-[hsl(var(--bone-2))] text-xs uppercase tracking-wider text-muted-foreground font-mono">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Applicant</th>
                    <th className="text-left px-4 py-3 font-medium">Position</th>
                    <th className="text-left px-4 py-3 font-medium">Location</th>
                    <th className="text-left px-4 py-3 font-medium">Submitted</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-right px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item._id}
                      className="border-t border-bone-2 hover:bg-[hsl(var(--bone-2)/0.6)] transition-colors"
                      data-testid={`admin-application-row-${item._id}`}
                    >
                      <td className="px-4 py-3">
                        <Link to={`/admin/applications/${item._id}`} className="block">
                          <p className="font-display font-semibold text-foreground">{item.full_name}</p>
                          <p className="text-xs text-muted-foreground">{item.email}</p>
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-foreground/85">{item.role_applying_for}</td>
                      <td className="px-4 py-3 text-foreground/85">{[item.address_city, item.address_state].filter(Boolean).join(', ') || '—'}</td>
                      <td className="px-4 py-3">
                        <p className="text-foreground/85" title={formatDateTime(item.submitted_at)}>
                          {formatRelative(item.submitted_at)}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className={`font-mono text-[10px] tracking-[0.18em] uppercase ${statusBadgeClass(item.status)}`}>
                          {item.status || 'new'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/applications/${item._id}`}
                          className="inline-flex items-center text-primary hover:underline text-xs gap-1"
                          data-testid={`admin-application-open-${item._id}`}
                        >
                          Open
                          <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
